import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check, Eye, ClipboardList, SlidersHorizontal, RefreshCw, Link2, Copy } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { getClientAssessment, setClientLevel, regenerateAssessment } from "@/lib/clientApi";

const LEVELS = [
  { id: "new", emoji: "🌱", title: "Just getting started", desc: "Gentle check, basics only" },
  { id: "some", emoji: "📊", title: "Knows the basics", desc: "Balanced check, unlock basic + intermediate" },
  { id: "confident", emoji: "🚀", title: "Confident with numbers", desc: "Deeper check, unlock all levels, harder learnings" },
];

export default function ClientDetailDialog({ clientId, open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [savingLevel, setSavingLevel] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(null);

  const fetchDetail = async () => {
    const d = await getClientAssessment(clientId);
    setData(d);
    setCurrentLevel(d.knowledge_level);
  };

  useEffect(() => {
    if (!open || !clientId) return;
    setData(null);
    fetchDetail().catch(() => toast.error("Could not load client details."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  const chooseLevel = async (lvl) => {
    setSavingLevel(lvl);
    setData(null);
    try {
      await setClientLevel(clientId, lvl);
      await fetchDetail();
      toast.success(`Set to "${LEVELS.find((l) => l.id === lvl).title}" and fresh questions generated.`);
      onChanged && onChanged();
    } catch (e) {
      toast.error("Could not update difficulty.");
      fetchDetail().catch(() => {});
    } finally {
      setSavingLevel(null);
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    setData(null);
    try {
      await regenerateAssessment(clientId);
      await fetchDetail();
      toast.success("Fresh set of questions generated.");
      onChanged && onChanged();
    } catch (e) {
      toast.error("Could not regenerate questions.");
      fetchDetail().catch(() => {});
    } finally {
      setRegenerating(false);
    }
  };

  const busy = savingLevel != null || regenerating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="client-detail-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#005580]">
            {data?.client_name || "Client"} {data?.business_name ? `· ${data.business_name}` : ""}
          </DialogTitle>
          <DialogDescription>
            Preview the understanding check, review answers, and set the difficulty for their learnings.
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-sky-400 animate-spin" /></div>
        ) : (
          <div className="space-y-6 mt-2">
            {/* Share links */}
            <section>
              <h4 className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">
                <Link2 className="w-4 h-4" /> Links to send
              </h4>
              <div className="space-y-2">
                <LinkRow
                  testid="copy-assess-link"
                  label="Understanding check"
                  sub="One-time check to find their level"
                  url={`${window.location.origin}/assess/${data.assessment_token}`}
                />
                <LinkRow
                  testid="copy-practice-link"
                  label="Practice link"
                  sub="Practise anytime, see answers as they go"
                  url={`${window.location.origin}/practice/${data.practice_token}`}
                />
              </div>
            </section>

            {/* Difficulty control */}
            <section>
              <h4 className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">
                <SlidersHorizontal className="w-4 h-4" /> Difficulty level
              </h4>
              <div className="space-y-2">
                {LEVELS.map((l) => {
                  const active = currentLevel === l.id;
                  return (
                    <button
                      key={l.id}
                      data-testid={`set-level-${l.id}`}
                      onClick={() => chooseLevel(l.id)}
                      disabled={busy}
                      className={`w-full flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-all ${
                        active ? "bg-sky-50 border-sky-400" : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <span className="text-2xl">{l.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-800">{l.title}</div>
                        <div className="text-xs text-slate-500 font-semibold">{l.desc}</div>
                      </div>
                      {savingLevel === l.id ? <Loader2 className="w-5 h-5 text-sky-500 animate-spin" />
                        : active ? <span className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center"><Check className="w-4 h-4 text-white" strokeWidth={3} /></span>
                        : null}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 font-semibold mt-2">
                Choosing a level generates a fresh set of check questions tailored to it, and unlocks the matching learnings so they don't have to start from the basics.
              </p>
            </section>

            {/* Questions preview / answers */}
            <section>
              <div className="flex items-center justify-between gap-3 mb-3">
                <h4 className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-slate-400">
                  {data.completed ? <><ClipboardList className="w-4 h-4" /> Their answers · {data.score}%</> : <><Eye className="w-4 h-4" /> Understanding check preview</>}
                </h4>
                <button
                  data-testid="regenerate-questions-btn"
                  onClick={regenerate}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-600 bg-sky-50 hover:bg-sky-100 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-60"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? "animate-spin" : ""}`} /> Reset & regenerate
                </button>
              </div>
              <div className="space-y-4">
                {data.questions.map((q, qi) => (
                  <div key={qi} className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                    <p className="font-bold text-slate-800 mb-3 text-[15px]"><span className="mr-1">{q.emoji}</span>{qi + 1}. {q.question}</p>
                    <div className="space-y-1.5">
                      {q.options.map((o, oi) => {
                        const chosen = data.completed && q.client_answer === oi;
                        const best = q.best_option === oi;
                        return (
                          <div key={oi}
                               className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm font-semibold border ${
                                 chosen ? "bg-sky-100 border-sky-300 text-sky-900" : "bg-white border-slate-200 text-slate-600"
                               }`}>
                            <span className="leading-snug">{o.text}</span>
                            <span className="flex items-center gap-1.5 shrink-0">
                              {best && <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide">most confident</span>}
                              {chosen && <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wide">chose</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {!data.completed && (
                <p className="text-xs text-slate-400 font-semibold mt-3">
                  The client hasn't completed this yet. You can still set their difficulty above to start their learnings now.
                </p>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LinkRow({ label, sub, url, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); }
    catch (e) {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setCopied(true);
    toast.success(`${label} copied. Send it to your client.`);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border-2 border-slate-100 bg-slate-50 px-4 py-3">
      <div className="min-w-0">
        <div className="font-bold text-slate-800 text-sm">{label}</div>
        <div className="text-xs text-slate-400 font-semibold truncate">{sub}</div>
      </div>
      <button
        data-testid={testid}
        onClick={copy}
        className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#29ABE2] hover:bg-[#1E92C7] rounded-xl px-3 py-2 transition-colors shrink-0"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
